// src/Jobs.tsx
// The caller's render jobs: status, progress, errors, and where the video
// ended up (a download for accounts whose renders are stored online, the
// local path for the owner's). Also exports the pieces the project page
// reuses: the jobs table, the worker-offline banner, and the polling hook.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isActiveMember } from '@ring/shared/author-trust';
import type { JobStatus } from '@ring/shared/projects';
import { listJobs, cancelJob, jobOutputUrl, downloadInChunks, type JobList, type JobSummary, type WorkerStatusSummary } from './projectsApi';
import './Projects.css';

/** Fast while something is queued or rendering; slow otherwise, just to keep the worker banner current. */
const POLL_ACTIVE_MS = 5_000;
const POLL_IDLE_MS   = 30_000;

const STATUS_LABEL: Record<JobStatus, string> = {
  queued:  'Waiting',
  running: 'Rendering',
  done:    'Done',
  failed:  'Failed',
  cancelled: 'Cancelled',
};

export function isActiveJob(job: JobSummary): boolean {
  return job.status === 'queued' || job.status === 'running';
}

/**
 * Loads jobs (all, or one project's) and keeps them fresh while the tab is
 * visible. `refresh` re-fetches now — call it right after queueing a render.
 */
export function useJobs(opts: { projectId?: string; limit?: number; enabled: boolean }) {
  const { projectId, limit, enabled } = opts;
  const [data, setData]   = useState<JobList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** False once unmounted, so a request still in flight then doesn't set state or start a new timer. */
  const alive = useRef(false);

  const refresh = useCallback(async () => {
    clearTimeout(timer.current);
    let next = POLL_IDLE_MS;
    try {
      const list = await listJobs({ projectId, limit });
      if (!alive.current) return;
      setData(list);
      setError(null);
      if (list.jobs.some(isActiveJob)) next = POLL_ACTIVE_MS;
    } catch (err) {
      if (!alive.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load renders.');
    }
    clearTimeout(timer.current); // a refresh() called while this one was in flight may have set one
    timer.current = setTimeout(function tick() {
      if (document.visibilityState === 'visible') void refresh();
      else timer.current = setTimeout(tick, next);
    }, next);
  }, [projectId, limit]);

  useEffect(() => {
    if (!enabled) return;
    alive.current = true;
    void refresh();
    return () => { alive.current = false; clearTimeout(timer.current); };
  }, [enabled, refresh]);

  return { jobs: data?.jobs ?? null, worker: data?.worker ?? null, error, refresh };
}

export function WorkerBanner({ worker, jobs }: { worker: WorkerStatusSummary | null; jobs: JobSummary[] | null }) {
  if (!worker || worker.online) return null;
  const waiting = jobs?.filter(isActiveJob).length ?? 0;
  return (
    <p className="page-notice projects-worker-banner">
      Renders are paused: the render machine is offline
      {worker.lastSeen && ` (last seen ${formatWhen(worker.lastSeen)})`}.
      {waiting > 0
        ? ` Your ${waiting === 1 ? 'render is' : `${waiting} renders are`} safe in the queue and will start when it's back.`
        : ' You can still queue renders; they\'ll start when it\'s back.'}
    </p>
  );
}

export function formatWhen(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function JobResult({ job }: { job: JobSummary }) {
  const [copied, setCopied] = useState(false);
  /** null when idle; 0–1 while the MP4 downloads. */
  const [downloading, setDownloading] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function download() {
    setDownloading(0);
    setDownloadError(null);
    try {
      await downloadInChunks(jobOutputUrl(job.jobId), setDownloading);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setDownloading(null);
    }
  }

  if (job.status === 'failed') return <pre className="projects-job-error">{job.error ?? 'Failed.'}</pre>;
  if (job.status !== 'done') {
    return job.error ? <span className="projects-job-note">{job.error}</span> : null;
  }
  if (job.hasOutput) {
    return (
      <span className="projects-job-links">
        <button type="button" disabled={downloading !== null} onClick={download}>
          {downloading === null ? 'Download MP4' : `Downloading… ${Math.round(downloading * 100)}%`}
        </button>
        <a href={jobOutputUrl(job.jobId, 'srt')} download>Captions (.srt)</a>
        {downloadError && <span className="page-error-text">{downloadError}</span>}
      </span>
    );
  }
  if (job.outputPath) {
    return (
      <span className="projects-job-path">
        <code>{job.outputPath}</code>
        <button
          type="button"
          onClick={() => navigator.clipboard.writeText(job.outputPath!).then(() => setCopied(true), () => {})}
        >
          {copied ? 'Copied' : 'Copy path'}
        </button>
      </span>
    );
  }
  return null;
}

/** Cancel for a render that hasn't started. If the worker picks it up first, the API says so (409). */
function CancelButton({ job, onCancelled }: { job: JobSummary; onCancelled: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function cancel() {
    setBusy(true);
    setError(null);
    try {
      await cancelJob(job.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn\'t cancel.');
    } finally {
      setBusy(false);
      onCancelled(); // refresh either way: if it started meanwhile, the list should say so
    }
  }
  return (
    <span className="projects-job-cancel">
      <button type="button" disabled={busy} onClick={cancel}>{busy ? 'Cancelling…' : 'Cancel'}</button>
      {error && <span className="page-error-text">{error}</span>}
    </span>
  );
}

export function JobsTable({ jobs, showProject, onChanged }: { jobs: JobSummary[]; showProject: boolean; onChanged: () => void }) {
  return (
    <ul className="projects-jobs">
      {jobs.map(job => (
        <li key={job.jobId} className="projects-job">
          <div className="projects-job-head">
            {showProject && (
              job.projectId
                ? <Link className="projects-job-project" to={`/dashboard/projects/${job.projectId}`}>{job.projectName ?? 'Untitled'}</Link>
                : <span className="projects-job-project">{job.projectName ?? 'Untitled'}</span>
            )}
            <span className={`projects-badge projects-badge-${job.status}`}>
              {job.status === 'running' && job.type === 'describe-assets' ? 'Describing' : STATUS_LABEL[job.status]}
              {job.status === 'running' && job.progress > 0 && ` ${Math.round(job.progress * 100)}%`}
            </span>
            <span className="projects-job-when">
              {formatWhen(job.createdAt)}
              {job.attempts > 1 && ` · try ${job.attempts}`}
            </span>
            {/* A description is taken out of the queue per asset, in the Library ("Write it myself") */}
            {job.status === 'queued' && job.type === 'render' && <CancelButton job={job} onCancelled={onChanged} />}
          </div>
          {job.status === 'running' && (
            <div className="projects-progress" aria-hidden="true">
              <span style={{ width: `${Math.round(job.progress * 100)}%` }} />
            </div>
          )}
          <JobResult job={job} />
        </li>
      ))}
    </ul>
  );
}

export function Jobs() {
  const { author, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const active = author ? isActiveMember(author.authorStatus) : false;
  const { jobs, worker, error, refresh } = useJobs({ enabled: active });

  useEffect(() => {
    if (!authLoading && (!author || !active)) navigate('/dashboard');
  }, [authLoading, author, active, navigate]);

  if (authLoading || !author || !active) return null;

  return (
    <div className="projects-page">
      <h1>Renders</h1>
      <p className="projects-sub">Final renders of your projects, newest first.</p>
      <WorkerBanner worker={worker} jobs={jobs} />
      {error && <p className="page-error-text">{error}</p>}
      {jobs === null ? (
        !error && <p className="projects-sub">Loading…</p>
      ) : jobs.length === 0 ? (
        <p className="projects-empty">
          Nothing rendered yet. Open a <Link to="/dashboard/projects">project</Link> and press Render.
        </p>
      ) : (
        <JobsTable jobs={jobs} showProject onChanged={() => void refresh()} />
      )}
    </div>
  );
}
